import { UserProfile, User, PermissionKey } from "@/types/permissions";

// Default system user profiles
export const mockUserProfiles: UserProfile[] = [
  {
    id: "profile-1",
    name: "Administrator",
    description: "Full access to all features and settings",
    isDefault: true,
    isSystem: true,
    permissions: [
      // Dashboard
      "dashboard.view",
      // Top Level
      "goods-in-out.view",
      "goods-in-out.create",
      "stock-assistant.view",
      "quick-start.view",
      // Inventory Management
      "inventory.stock-items.view",
      "inventory.stock-items.create",
      "inventory.stock-items.edit",
      "inventory.stock-items.delete",
      "inventory.non-stock-items.view",
      "inventory.non-stock-items.create",
      "inventory.non-stock-items.edit",
      "inventory.non-stock-items.delete",
      "inventory.stock-transfers.view",
      "inventory.stock-transfers.create",
      "inventory.stock-transfers.edit",
      "inventory.stock-transfers.delete",
      "inventory.movements.view",
      "inventory.movements.create",
      "inventory.movements.edit",
      "inventory.movements.delete",
      "inventory.tracking.view",
      "inventory.tracking.edit",
      // Warehouse Operations
      "warehouse.receive-goods.view",
      "warehouse.receive-goods.create",
      "warehouse.locations.view",
      "warehouse.locations.create",
      "warehouse.locations.edit",
      "warehouse.locations.delete",
      "warehouse.stock-takes.view",
      "warehouse.stock-takes.create",
      "warehouse.stock-takes.edit",
      "warehouse.stock-takes.delete",
      // Purchasing
      "purchasing.orders.view",
      "purchasing.orders.create",
      "purchasing.orders.edit",
      "purchasing.orders.delete",
      "purchasing.orders.approve",
      "purchasing.suppliers.view",
      "purchasing.suppliers.create",
      "purchasing.suppliers.edit",
      "purchasing.suppliers.delete",
      "purchasing.suppliers.view-credentials",
      "purchasing.suppliers.edit-credentials",
      // Sales
      "sales.orders.view",
      "sales.orders.create",
      "sales.orders.edit",
      "sales.orders.delete",
      "sales.orders.approve",
      "sales.customers.view",
      "sales.customers.create",
      "sales.customers.edit",
      "sales.customers.delete",
      // Manufacturing
      "manufacturing.boms.view",
      "manufacturing.boms.create",
      "manufacturing.boms.edit",
      "manufacturing.boms.delete",
      "manufacturing.work-orders.view",
      "manufacturing.work-orders.create",
      "manufacturing.work-orders.edit",
      "manufacturing.work-orders.delete",
      // Projects
      "projects.view",
      "projects.create",
      "projects.edit",
      "projects.delete",
      // Analytics
      "analytics.forecasting.view",
      "analytics.abc-classification.view",
      "analytics.reorder.view",
      "analytics.reorder.approve",
      "analytics.margin-analysis.view",
      "analytics.cost-analysis.view",
      // Pricing
      "pricing.matrix.view",
      "pricing.matrix.edit",
      "pricing.advanced.view",
      "pricing.advanced.edit",
      // Settings
      "settings.system-config.view",
      "settings.system-config.edit",
      "settings.location-settings.view",
      "settings.location-settings.edit",
      "settings.integration.view",
      "settings.integration.edit",
      "settings.company.view",
      "settings.company.edit",
      "settings.user-management.view",
      "settings.user-management.create",
      "settings.user-management.edit",
      "settings.user-management.delete",
      "settings.custom-fields.view",
      "settings.custom-fields.create",
      "settings.custom-fields.edit",
      "settings.custom-fields.delete",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-2",
    name: "Warehouse Manager",
    description: "Manage inventory, stock movements, and warehouse operations",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "goods-in-out.view",
      "goods-in-out.create",
      "inventory.stock-items.view",
      "inventory.stock-items.create",
      "inventory.stock-items.edit",
      "inventory.stock-transfers.view",
      "inventory.stock-transfers.create",
      "inventory.movements.view",
      "inventory.movements.create",
      "inventory.movements.edit",
      "inventory.tracking.view",
      "inventory.tracking.edit",
      "warehouse.receive-goods.view",
      "warehouse.receive-goods.create",
      "warehouse.locations.view",
      "warehouse.locations.create",
      "warehouse.locations.edit",
      "warehouse.stock-takes.view",
      "warehouse.stock-takes.create",
      "warehouse.stock-takes.edit",
      "settings.company.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-3",
    name: "Purchasing Manager",
    description: "Manage purchase orders, suppliers, and goods receipt",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "inventory.stock-items.view",
      "warehouse.receive-goods.view",
      "warehouse.receive-goods.create",
      "purchasing.orders.view",
      "purchasing.orders.create",
      "purchasing.orders.edit",
      "purchasing.orders.delete",
      "purchasing.orders.approve",
      "purchasing.suppliers.view",
      "purchasing.suppliers.create",
      "purchasing.suppliers.edit",
      "purchasing.suppliers.delete",
      "purchasing.suppliers.view-credentials",
      "purchasing.suppliers.edit-credentials",
      "projects.view",
      "analytics.cost-analysis.view",
      "settings.company.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-4",
    name: "Sales Manager",
    description: "Manage sales orders, customers, and fulfillment",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "inventory.stock-items.view",
      "sales.orders.view",
      "sales.orders.create",
      "sales.orders.edit",
      "sales.orders.delete",
      "sales.orders.approve",
      "sales.customers.view",
      "sales.customers.create",
      "sales.customers.edit",
      "sales.customers.delete",
      "projects.view",
      "pricing.matrix.view",
      "pricing.advanced.view",
      "analytics.margin-analysis.view",
      "settings.company.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-5",
    name: "Production Manager",
    description: "Manage manufacturing, BOMs, and production scheduling",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "inventory.stock-items.view",
      "inventory.movements.view",
      "manufacturing.boms.view",
      "manufacturing.boms.create",
      "manufacturing.boms.edit",
      "manufacturing.boms.delete",
      "manufacturing.work-orders.view",
      "manufacturing.work-orders.create",
      "manufacturing.work-orders.edit",
      "manufacturing.work-orders.delete",
      "projects.view",
      "settings.company.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-6",
    name: "Warehouse Operator",
    description: "View-only access to inventory with basic movement creation",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "goods-in-out.view",
      "inventory.stock-items.view",
      "inventory.movements.view",
      "inventory.movements.create",
      "inventory.tracking.view",
      "warehouse.locations.view",
      "warehouse.stock-takes.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: "profile-7",
    name: "Analyst",
    description: "View-only access to reports, analytics, and forecasting",
    isDefault: true,
    isSystem: true,
    permissions: [
      "dashboard.view",
      "inventory.stock-items.view",
      "inventory.movements.view",
      "analytics.forecasting.view",
      "analytics.abc-classification.view",
      "analytics.reorder.view",
      "analytics.margin-analysis.view",
      "analytics.cost-analysis.view",
      "pricing.matrix.view",
      "pricing.advanced.view",
      "settings.company.view",
    ] as PermissionKey[],
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
];

// Mock users
export const mockUsers: User[] = [
  {
    id: "user-1",
    name: "John Admin",
    email: "john.admin@stockevo.com",
    profileId: "profile-1",
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    lastLogin: "2024-01-15T10:30:00Z",
  },
  {
    id: "user-2",
    name: "Sarah Warehouse",
    email: "sarah.warehouse@stockevo.com",
    profileId: "profile-2",
    isActive: true,
    createdAt: "2024-01-02T00:00:00Z",
    lastLogin: "2024-01-15T09:15:00Z",
  },
  {
    id: "user-3",
    name: "Mike Purchasing",
    email: "mike.purchasing@stockevo.com",
    profileId: "profile-3",
    isActive: true,
    createdAt: "2024-01-03T00:00:00Z",
    lastLogin: "2024-01-15T08:45:00Z",
  },
  {
    id: "user-4",
    name: "Emma Sales",
    email: "emma.sales@stockevo.com",
    profileId: "profile-4",
    isActive: true,
    createdAt: "2024-01-04T00:00:00Z",
    lastLogin: "2024-01-14T16:20:00Z",
  },
  {
    id: "user-5",
    name: "David Production",
    email: "david.production@stockevo.com",
    profileId: "profile-5",
    isActive: true,
    createdAt: "2024-01-05T00:00:00Z",
    lastLogin: "2024-01-15T07:30:00Z",
  },
  {
    id: "user-6",
    name: "Lisa Operator",
    email: "lisa.operator@stockevo.com",
    profileId: "profile-6",
    isActive: true,
    createdAt: "2024-01-06T00:00:00Z",
    lastLogin: "2024-01-15T11:00:00Z",
  },
  {
    id: "user-7",
    name: "Tom Analyst",
    email: "tom.analyst@stockevo.com",
    profileId: "profile-7",
    isActive: true,
    createdAt: "2024-01-07T00:00:00Z",
    lastLogin: "2024-01-15T09:45:00Z",
  },
  {
    id: "user-8",
    name: "Rachel Inactive",
    email: "rachel.inactive@stockevo.com",
    profileId: "profile-6",
    isActive: false,
    createdAt: "2024-01-08T00:00:00Z",
    lastLogin: "2024-01-10T14:20:00Z",
  },
];
